import { Spacing } from '@persona-id/design-tokens';
import { countBy, identity, join, map, pipe, toPairs } from 'lodash/fp';
import { Duration } from 'luxon';
import { useMemo } from 'react';
import styled from 'styled-components';

import { VERIFICATION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { Inquiry, InquiryStatus } from 'dashboard/models/inquiry';
import { ListMatch } from 'dashboard/models/list-match';
import { Verification } from 'dashboard/models/verification';
import Card from 'design-system/components/Card';
import { MINUTES_SECONDS_ELAPSED_FORMAT } from 'lib/constants/luxonFormats';

const ColumnContainer = styled('div')`
  display: grid;
  grid-column-gap: ${Spacing[4]};
  grid-template-columns: 1fr 1fr 1fr;
  width: 80%;
`;

interface InquirySummaryProps {
  inquiry: Inquiry;
  listMatches: Record<string, ListMatch>;
  verifications: Verification[];
}

export default function InquirySummary({
  inquiry,
  listMatches,
  verifications,
}: InquirySummaryProps) {
  const { status, 'completion-time': inquiryCompletionTime } = inquiry.attributes;
  const inquiryIsPending =
    status != null && [InquiryStatus.Created, InquiryStatus.Pending].includes(status);
  const listMatchesCount = Object.keys(listMatches).length;

  const completionTime = useMemo(() => {
    if (inquiryCompletionTime) {
      return Duration.fromObject({ seconds: inquiryCompletionTime }).toFormat(
        MINUTES_SECONDS_ELAPSED_FORMAT,
      );
    } else if (inquiryIsPending) {
      return 'Pending';
    } else {
      return 'N/A';
    }
  }, [inquiryCompletionTime, inquiryIsPending]);

  type AttemptsPerType = [Verification['type'], number];
  const getAttemptsPerType = pipe<
    [Verification[]],
    Array<Verification['type']>,
    Record<Verification['type'], number>,
    AttemptsPerType[],
    AttemptsPerType[],
    string[],
    string
  >(
    map('type'),
    (types) => countBy(identity, types) as Record<Verification['type'], number>,
    (count) => toPairs(count) as AttemptsPerType[],
    (attemptsPerType) => attemptsPerType.sort((a, b) => b[1] - a[1]),
    map(([type, count]) => `${VERIFICATION_TYPE_NAMES_SHORT[type]}: ${count}`),
    join(', '),
  );

  const verificationsText = verifications.length > 0 ? getAttemptsPerType(verifications) : 'N/A';

  return (
    <Card>
      <Card.Body>
        <Card.Title>Summary</Card.Title>
        <ColumnContainer>
          <div aria-labelledby='inquiry-summary__lists--label'>
            <Card.Subtitle id='inquiry-summary__lists--label'>List Matches</Card.Subtitle>
            <Card.Title data-test='inquiry-summary__lists'>
              {inquiryIsPending ? 'Pending' : listMatchesCount}
            </Card.Title>
          </div>
          <div aria-labelledby='inquiry-summary__completion-time--label'>
            <Card.Subtitle id='inquiry-summary__completion-time--label'>
              Time to Finish
            </Card.Subtitle>
            <Card.Title data-test='inquiry-summary__completion-time'>{completionTime}</Card.Title>
          </div>
          <div aria-labelledby='inquiry-summary__attempts--label'>
            <Card.Subtitle id='inquiry-summary__attempts--label'>
              Verification Attempts
            </Card.Subtitle>
            <Card.Title data-test='inquiry-summary__attempts'>
              {inquiryIsPending ? 'Pending' : verificationsText}{' '}
            </Card.Title>
          </div>
        </ColumnContainer>
      </Card.Body>
    </Card>
  );
}
